/**
 * AudioWorklet ring buffer for QoS audio resilience.
 *
 * Captures PCM samples into a circular buffer (15s @ 48kHz mono = ~1.4MB).
 * When the network connection drops, samples accumulate in the buffer.
 * On reconnection, the buffered audio can be played back at 2x speed.
 *
 * Usage:
 *   const ctx = new AudioContext()
 *   await ctx.audioWorklet.addModule(audioBufferProcessorUrl)
 *   const node = new AudioWorkletNode(ctx, 'audio-buffer-processor')
 *   // node.port.postMessage({ command: 'startBuffering' })
 *   // node.port.postMessage({ command: 'stopBuffering' })
 *   // node.port.postMessage({ command: 'flush' })  -> returns buffered PCM
 */

// Ring buffer size: 15 seconds at 48kHz mono
const SAMPLE_RATE = 48000
const BUFFER_SECONDS = 15
const RING_SIZE = SAMPLE_RATE * BUFFER_SECONDS

// AudioWorklet processor code as a string (will be loaded via Blob URL)
const processorCode = `
class AudioBufferProcessor extends AudioWorkletProcessor {
  constructor() {
    super()
    this._ring = new Float32Array(${RING_SIZE})
    this._writeHead = 0
    this._bufferedSamples = 0
    this._buffering = false

    this.port.onmessage = (e) => {
      const { command } = e.data
      if (command === 'startBuffering') {
        this._buffering = true
        this._bufferedSamples = 0
        this._writeHead = 0
      } else if (command === 'stopBuffering') {
        this._buffering = false
      } else if (command === 'flush') {
        // Return buffered PCM and reset
        const count = Math.min(this._bufferedSamples, ${RING_SIZE})
        const out = new Float32Array(count)
        // Read from ring buffer starting at oldest sample
        const start = count >= ${RING_SIZE}
          ? this._writeHead  // wrapped: oldest is at writeHead
          : 0
        for (let i = 0; i < count; i++) {
          out[i] = this._ring[(start + i) % ${RING_SIZE}]
        }
        this.port.postMessage({ type: 'buffer', samples: out, sampleRate: ${SAMPLE_RATE} }, [out.buffer])
        this._bufferedSamples = 0
        this._writeHead = 0
      } else if (command === 'getStatus') {
        this.port.postMessage({
          type: 'status',
          bufferedSeconds: Math.min(this._bufferedSamples, ${RING_SIZE}) / ${SAMPLE_RATE},
          buffering: this._buffering,
        })
      }
    }
  }

  process(inputs, outputs) {
    const input = inputs[0]
    if (!input || !input[0]) return true

    const channel = input[0]

    if (this._buffering) {
      // Write incoming samples to ring buffer
      for (let i = 0; i < channel.length; i++) {
        this._ring[this._writeHead] = channel[i]
        this._writeHead = (this._writeHead + 1) % ${RING_SIZE}
        this._bufferedSamples++
      }
    }

    // Pass through audio unmodified
    const output = outputs[0]
    if (output && output[0]) {
      output[0].set(channel)
    }

    return true
  }
}

registerProcessor('audio-buffer-processor', AudioBufferProcessor)
`

let _blobUrl: string | null = null

/** Get the Blob URL for the AudioWorklet processor module. */
export function getProcessorUrl(): string {
  if (!_blobUrl) {
    const blob = new Blob([processorCode], { type: 'application/javascript' })
    _blobUrl = URL.createObjectURL(blob)
  }
  return _blobUrl
}

/** Play buffered PCM at 2x speed, mixed into the given AudioContext destination. */
export function playBufferAt2x(
  ctx: AudioContext,
  samples: Float32Array,
  sampleRate: number,
  onEnded?: () => void,
): AudioBufferSourceNode | null {
  if (samples.length === 0) return null

  const audioBuffer = ctx.createBuffer(1, samples.length, sampleRate)
  audioBuffer.getChannelData(0).set(samples)

  const source = ctx.createBufferSource()
  source.buffer = audioBuffer
  source.playbackRate.value = 2.0
  // Preserve pitch so voice doesn't sound chipmunk
  if ('preservesPitch' in source) {
    ;(source as any).preservesPitch = true
  }
  source.connect(ctx.destination)
  source.onended = () => onEnded?.()
  source.start()
  return source
}

export { SAMPLE_RATE, BUFFER_SECONDS, RING_SIZE }
